import { PILLARS, calculateNextIndex, getScoreColor, getTier } from '../utils/scoring';

export default function ExecutiveSummary({ audit }) {
  const scores = audit.scores || {};
  const intake = audit.intake || {};
  const ai = audit.aiResearch || {};
  const nextIndex = calculateNextIndex(scores);
  const tier = getTier(nextIndex);
  const indexColor = getScoreColor(nextIndex);

  const sorted = [...PILLARS].map(p => ({ ...p, score: scores[p.id]?.score || 0 })).filter(s => s.score > 0).sort((a, b) => b.score - a.score);
  const top3 = sorted.slice(0, 3);
  const bottom3 = sorted.slice(-3).reverse();

  const roadmap = ai.roadmap?.immediate || [];

  const clientUrl = `${window.location.origin}/client/${audit.id}`;

  return (
    <div id="exec-summary" className="bg-black text-white" style={{ width: '8.5in', maxWidth: '100vw', minHeight: '11in', padding: '0.6in', fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #333', paddingBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="NE" style={{ height: '28px' }} />
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#C9A668', fontFamily: "'Libre Baskerville', Georgia, serif" }}>What's NEXT</div>
            <div style={{ fontSize: '9px', color: '#666', letterSpacing: '0.15em' }}>EXECUTIVE SUMMARY</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: '#fff' }}>{intake.conceptName}</div>
          <div style={{ fontSize: '9px', color: '#888' }}>{intake.city}</div>
        </div>
      </div>

      {/* Score + Pillar Bars */}
      <div style={{ display: 'flex', gap: '32px', marginBottom: '28px' }}>
        {/* Score */}
        <div style={{ textAlign: 'center', minWidth: '120px' }}>
          <div style={{
            width: '100px', height: '100px', borderRadius: '50%',
            border: `4px solid ${indexColor.hex || '#C9A668'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 8px',
          }}>
            <div>
              <div style={{ fontSize: '32px', fontWeight: 900, color: indexColor.hex || '#C9A668', lineHeight: 1 }}>{nextIndex.toFixed(1)}</div>
              <div style={{ fontSize: '8px', color: '#888', marginTop: '2px' }}>NEXT INDEX</div>
            </div>
          </div>
          <div style={{ fontSize: '11px', fontWeight: 600, color: tier.color }}>{tier.name}</div>
        </div>

        {/* Pillar Bars */}
        <div style={{ flex: 1 }}>
          {PILLARS.map(p => {
            const s = scores[p.id]?.score || 0;
            const c = getScoreColor(s);
            const pct = (s / 10) * 100;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <div style={{ fontSize: '8px', color: '#999', width: '120px', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ flex: 1, height: '10px', background: '#222', borderRadius: '5px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', backgroundColor: c.hex || '#555', borderRadius: '5px', transition: 'width 0.3s' }} />
                </div>
                <div style={{ fontSize: '9px', fontWeight: 700, color: c.hex || '#555', width: '24px', textAlign: 'right' }}>{s > 0 ? s.toFixed(1) : '—'}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Executive Diagnostic */}
      {ai.executiveDiagnostic && (
        <div style={{ marginBottom: '24px', padding: '14px 16px', borderLeft: '3px solid #C9A668', background: '#111', borderRadius: '0 8px 8px 0' }}>
          <div style={{ fontSize: '9px', color: '#C9A668', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '6px' }}>EXECUTIVE DIAGNOSTIC</div>
          <div style={{ fontSize: '11px', color: '#ccc', lineHeight: 1.5 }}>{ai.executiveDiagnostic}</div>
        </div>
      )}

      {/* Strengths + Focus Areas */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        <div style={{ flex: 1, background: '#0a0a0a', border: '1px solid #222', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '9px', color: '#999', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '10px' }}>TOP SCORES</div>
          {top3.map(p => {
            const c = getScoreColor(p.score);
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '10px', color: '#ccc' }}>{p.name}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: c.hex || '#888' }}>{p.score.toFixed(1)}</span>
              </div>
            );
          })}
        </div>
        <div style={{ flex: 1, background: '#0a0a0a', border: '1px solid #222', borderRadius: '8px', padding: '14px' }}>
          <div style={{ fontSize: '9px', color: '#999', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '10px' }}>FOCUS AREAS</div>
          {bottom3.map(p => {
            const c = getScoreColor(p.score);
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ fontSize: '10px', color: '#ccc' }}>{p.name}</span>
                <span style={{ fontSize: '11px', fontWeight: 700, color: c.hex || '#888' }}>{p.score.toFixed(1)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Immediate Actions */}
      {roadmap.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '9px', color: '#C9A668', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '10px' }}>IMMEDIATE ACTIONS (0-30 DAYS)</div>
          {roadmap.filter(Boolean).slice(0, 4).map((item, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '10px', color: '#34d399', marginTop: '1px' }}>→</span>
              <span style={{ fontSize: '10px', color: '#bbb', lineHeight: 1.4 }}>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Score Tensions */}
      {ai.scoreTensions?.filter(Boolean).length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '9px', color: '#fbbf24', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }}>KEY TENSIONS</div>
          {ai.scoreTensions.filter(Boolean).slice(0, 2).map((t, i) => (
            <div key={i} style={{ fontSize: '10px', color: '#999', marginBottom: '4px', lineHeight: 1.4 }}>
              ! {typeof t === 'string' ? t : t.tension || t.description || ''}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={{ borderTop: '1px solid #333', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png" alt="NE" style={{ height: '16px' }} />
          <span style={{ fontSize: '9px', color: '#666' }}>Next Experience Group — nextexperience.group</span>
        </div>
        <div style={{ fontSize: '8px', color: '#555', textAlign: 'right' }}>
          <div>Full report: {clientUrl}</div>
          <div style={{ marginTop: '2px' }}>Confidential — {new Date(audit.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        </div>
      </div>
    </div>
  );
}
